import { JUDGE_POSITIONS } from "score-common";

// #627 — a scribe's judge position is a property of the CLASS, not the session.
// score-api (#396) stores it as Class.scribeAssignments[position] = [userId],
// where userId is the assignee's Cognito sub; scoreadmin (#398) writes it. The
// scribe never picks a position — we derive it from the class they're on.

// Every position this scribe is assigned at in the class. Normally one; a class
// can name the same person at two positions (e.g. the C and E seats of a
// two-judge young-horse class), and then they have to say which seat they're in.
export function scribePositionsFor(klass, sub) {
  if (!klass || !sub) return [];
  return JUDGE_POSITIONS.filter((p) =>
    (klass.scribeAssignments?.[p] ?? []).includes(sub),
  );
}

// The scribe's position when it's unambiguous, else null (0 assignments, or the
// multi-seat case that needs an explicit choice).
export function solePositionFor(klass, sub) {
  const positions = scribePositionsFor(klass, sub);
  return positions.length === 1 ? positions[0] : null;
}

export function isAssignedTo(klass, sub) {
  return scribePositionsFor(klass, sub).length > 0;
}

// #885 — every position actually JUDGED in this class (Class.judgeAssignments,
// #396's ORIGIN — the FVD-provided judge roster), not scribe-ASSIGNED. Used
// to pick a scoresheet to view read-only when a scribe has no seat of their
// own (scribePositionsFor returns []) but the ride is FINAL: there's no
// "my position" to default to, so the candidate list is every position a
// judge actually sat at. Mirrors expectedJudgeCountForClass's own
// `ids?.length > 0` truthiness check (score-common/utils/ride-status.js) so
// both agree on what counts as "judged" — judgeAssignments values are the
// judge's NAME per position (a string), not an array, hence `.length` rather
// than an array-specific check.
export function judgedPositionsFor(klass) {
  const assignments = klass?.judgeAssignments;
  if (!assignments) return [];
  return JUDGE_POSITIONS.filter((p) => (assignments[p]?.length ?? 0) > 0);
}

// #2191 — every position this class actually names a SCRIBE for
// (Class.scribeAssignments, #396), regardless of who. Used by
// verifyStillAssignedScribe's live re-check when the scribe pressing Start
// Scoring has no position of their own (a never-assigned scribe reaching the
// #2192 gate via the #2191 relaxed unassigned fall-through) — there's no
// single "my position" to check, so this checks every position the class
// names a scribe for instead.
export function scribeAssignedPositionsFor(klass) {
  const assignments = klass?.scribeAssignments;
  if (!assignments) return [];
  return JUDGE_POSITIONS.filter((p) => (assignments[p]?.length ?? 0) > 0);
}
